import { useEffect, useRef } from 'react';

const DEFAULT_LANGUAGE = 'en';

interface ISpotLocalizationEntry {
  url?: string;
  location?: string;
  previewImage?: string;
  id?: string;
  md5?: string;
}

type SpotLocalizationsShape = Record<string, ISpotLocalizationEntry>;

interface ILocalizableSpot {
  type?: string;
  data?: {
    location?: string;
    url?: string;
    id?: string;
    [k: string]: unknown;
  };
  previewImage?: string;
  md5?: string;
  localizations?: SpotLocalizationsShape;
  brandingSpotId?: string;
  roomSpotId?: string;
  /**
   * Pristine, pre-localization media captured the first time this spot is
   * localized. Every subsequent call derives its result from this baseline
   * (never from the previous call's output), so switching back to the
   * default language restores the true default instead of whatever the
   * last-applied override left behind.
   */
  originalMedia?: ISpotLocalizationEntry;
  [k: string]: unknown;
}

const isVideoSpot = (spot: ILocalizableSpot): boolean => {
  const upper = (spot.type ?? '').toUpperCase();
  return upper === 'VIDEO' || upper === 'VIDEO_STATIC';
};

export const localizeSpotForLanguage = (
  spot: ILocalizableSpot,
  languageCode: string,
): ILocalizableSpot => {
  const overrides = spot.localizations;
  if (!overrides) {
    return spot;
  }

  const originalMedia: ISpotLocalizationEntry = spot.originalMedia ?? {
    url: spot.data?.url,
    location: spot.data?.location,
    id: spot.data?.id,
    previewImage: spot.previewImage,
    md5: spot.md5,
  };

  const override =
    languageCode === DEFAULT_LANGUAGE
      ? overrides[DEFAULT_LANGUAGE]
      : overrides[languageCode] ?? overrides[DEFAULT_LANGUAGE];

  const effective = override ?? originalMedia;
  if (!effective || (!effective.url && !effective.location)) {
    return { ...spot, originalMedia };
  }

  const nextId = effective.id ?? originalMedia.id;
  const localizedData = {
    ...(spot.data ?? {}),
    url: effective.url ?? originalMedia.url,
    location: effective.location ?? originalMedia.location,
    ...(nextId ? { id: nextId } : {}),
  };

  const nextMd5 = effective.md5 ?? originalMedia.md5;
  const next: ILocalizableSpot = {
    ...spot,
    data: localizedData,
    originalMedia,
    ...(nextMd5 ? { md5: nextMd5 } : {}),
  };

  if (isVideoSpot(spot)) {
    const nextPreviewImage =
      effective.previewImage ?? originalMedia.previewImage;
    if (nextPreviewImage) {
      next.previewImage = nextPreviewImage;
    }
  }

  return next;
};

const localizeSpotList = (
  spots: ILocalizableSpot[],
  languageCode: string,
): ILocalizableSpot[] =>
  spots.map((spot) => localizeSpotForLanguage(spot, languageCode));

interface UseSpotLanguageSyncArgs<TSpot extends ILocalizableSpot> {
  language: string | undefined;
  isEventActive: boolean;
  brandingSpots: TSpot[];
  roomSpots: TSpot[];
  setBrandingSpots: (spots: TSpot[]) => void;
  setRoomSpots: (spots: TSpot[]) => void;
  sendMessageToUnreal: (message: Record<string, unknown>) => void;
}

export const useSpotLanguageSync = <TSpot extends ILocalizableSpot>({
  language,
  isEventActive,
  brandingSpots,
  roomSpots,
  setBrandingSpots,
  setRoomSpots,
  sendMessageToUnreal,
}: UseSpotLanguageSyncArgs<TSpot>): void => {
  const previousLanguageRef = useRef<string | null>(null);

  useEffect(() => {
    if (!language) {
      return;
    }

    if (previousLanguageRef.current === null) {
      previousLanguageRef.current = language;
      return;
    }

    if (previousLanguageRef.current === language) {
      return;
    }

    previousLanguageRef.current = language;

    if (!isEventActive) {
      return;
    }

    const nextBrandingSpots = localizeSpotList(
      brandingSpots,
      language,
    ) as TSpot[];
    const nextRoomSpots = localizeSpotList(roomSpots, language) as TSpot[];

    setBrandingSpots(nextBrandingSpots);
    setRoomSpots(nextRoomSpots);

    sendMessageToUnreal({
      action: 'CLEAR_SPOTS',
    });
    sendMessageToUnreal({
      action: 'SPOTS_UPDATED',
      spots: [],
    });
  }, [
    language,
    isEventActive,
    brandingSpots,
    roomSpots,
    setBrandingSpots,
    setRoomSpots,
    sendMessageToUnreal,
  ]);
};
